import { apiUrl } from './api/client'
import { formatDate, formatDateTime } from './format'
import type { DentMarker } from './components/AircraftDentView'

export interface AircraftDentAttachment {
  attachmentId: number
  filename: string
  contentType: string
}

export interface AircraftDent {
  dentId: number
  registration: string
  x: number
  y: number
  z: number | null
  note: string
  createdAt: string
  // Only sent to those who manage dents; null for everyone else.
  createdByName: string | null
  attachments: AircraftDentAttachment[]
}

// '3d' when the aircraft's type has a model, else '2d' when it has a
// silhouette, else null: there is nothing to show dents on.
export interface AircraftDents {
  view: '3d' | '2d' | null
  aircraftType: string | null
  dents: AircraftDent[]
}

// The type's .glb or silhouette, served by GET /api/aircraft-dents/…. The
// upload time is the cache-buster, as for the fleet photo.
export function dentAssetUrl(kind: 'models' | 'silhouettes', aircraftType: string, updatedAt?: string | null): string {
  const v = updatedAt ? `?v=${Date.parse(updatedAt)}` : ''
  return apiUrl(`/api/aircraft-dents/${kind}/${encodeURIComponent(aircraftType)}${v}`)
}

export const dentAttachmentUrl = (attachmentId: number) => apiUrl(`/api/aircraft-dents/attachments/${attachmentId}`)

// The viewer's markers for a register: numbered in list order, with the
// note and the first photo for the hover card.
//
// The hover card says when it was recorded. `dateOnly` (the member app)
// shows just the date; otherwise the time and who as well, when known.
export function dentMarkers(dents: AircraftDent[], { dateOnly = false }: { dateOnly?: boolean } = {}): DentMarker[] {
  return dents.map((d, index) => {
    const photo = d.attachments.find((a) => a.contentType.startsWith('image/'))
    return {
      dentId: d.dentId,
      x: d.x,
      y: d.y,
      z: d.z,
      label: index + 1,
      note: d.note,
      meta: !dateOnly && d.createdByName ? `${formatDateTime(d.createdAt)} · ${d.createdByName}` : formatDate(d.createdAt),
      photoUrl: photo ? dentAttachmentUrl(photo.attachmentId) : null,
    }
  })
}
